/**
 * Talker spend history from Sharetribe customer orders (talksession).
 * Same transactions as Inbox orders — no new billing backend.
 * ListingPage-style status: hold / charged / refunded / failed / hold released.
 */

import { getProcess, resolveLatestProcessName } from '../transactions/transaction';
import { talkSessionOtherPartyDisplayName } from './listingCardCopy';
import {
  isTalkSessionHoldRelease,
  isTalkSessionTransaction,
  resolveSessionFormat,
  talkSessionWasCaptured,
} from './sessionPricing';
import { types as sdkTypes } from './sdkLoader';

const { Money } = sdkTypes;

export const PAYMENT_HISTORY_HOLD = 'hold';
export const PAYMENT_HISTORY_CHARGED = 'charged';
export const PAYMENT_HISTORY_REFUNDED = 'refunded';
export const PAYMENT_HISTORY_FAILED = 'failed';
export const PAYMENT_HISTORY_RELEASED = 'released';

const FAILED_TRANSITIONS = new Set(['transition/expire-payment']);
const REFUND_TRANSITIONS = new Set([
  'transition/refund',
  'transition/operator-refund',
  'transition/cancel-from-disputed',
  'transition/auto-cancel-from-disputed',
]);

const amountOf = money => {
  const raw = money?.amount;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (raw && typeof raw.toNumber === 'function') {
    return raw.toNumber();
  }
  if (raw != null) {
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const processStateOf = tx => {
  try {
    return getProcess(resolveLatestProcessName(tx?.attributes?.processName)).getState(tx);
  } catch (e) {
    return null;
  }
};

const transitionsOf = tx => {
  const last = tx?.attributes?.lastTransition;
  const listed = tx?.attributes?.transitions || [];
  return [last, ...listed.map(item => item?.transition)].filter(Boolean);
};

/**
 * True when a captured talksession later reversed the customer payin.
 *
 * @param {Object} [tx]
 * @returns {boolean}
 */
export const talkSessionWasRefunded = tx => {
  if (!talkSessionWasCaptured(tx)) {
    return false;
  }
  if (transitionsOf(tx).some(name => REFUND_TRANSITIONS.has(name))) {
    return true;
  }
  return (tx?.attributes?.lineItems || []).some(
    item => item?.reversal === true && (item.includeFor || []).includes('customer')
  );
};

/**
 * Hold / charged / refunded / failed / hold-released, or null to skip.
 *
 * @param {Object} [tx]
 * @returns {string|null}
 */
export const paymentHistoryStatus = tx => {
  if (!isTalkSessionTransaction(tx)) {
    return null;
  }
  const last = tx?.attributes?.lastTransition;
  const state = processStateOf(tx);
  if (FAILED_TRANSITIONS.has(last) || state === 'payment-expired') {
    return PAYMENT_HISTORY_FAILED;
  }
  if (talkSessionWasRefunded(tx)) {
    return PAYMENT_HISTORY_REFUNDED;
  }
  if (talkSessionWasCaptured(tx)) {
    return PAYMENT_HISTORY_CHARGED;
  }
  if (isTalkSessionHoldRelease(tx)) {
    return PAYMENT_HISTORY_RELEASED;
  }
  if (state === 'inquiry' || state === 'pending-payment') {
    return null;
  }
  if (state === 'preauthorized' || state === 'accepted') {
    return PAYMENT_HISTORY_HOLD;
  }
  if (amountOf(tx?.attributes?.payinTotal) > 0 && state !== 'declined') {
    return PAYMENT_HISTORY_HOLD;
  }
  return null;
};

const paymentWhen = tx => {
  const booking = tx?.booking?.attributes || {};
  const raw =
    booking.displayStart ||
    booking.start ||
    tx?.attributes?.lastTransitionedAt ||
    tx?.attributes?.createdAt;
  const date = raw ? new Date(raw) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const payinMoney = tx => {
  const money = tx?.attributes?.payinTotal;
  if (money instanceof Money) {
    return money;
  }
  if (money?.currency) {
    return new Money(amountOf(money), money.currency);
  }
  return null;
};

/**
 * Customer talk-session payments for Payment & payouts.
 *
 * @param {Array<Object>} [transactions]
 * @returns {Array<Object>}
 */
export const listCustomerPaymentHistory = (transactions = []) => {
  const rows = [];
  (Array.isArray(transactions) ? transactions : []).forEach(tx => {
    const status = paymentHistoryStatus(tx);
    if (!status) {
      return;
    }
    const id = tx?.id?.uuid;
    if (!id) {
      return;
    }
    const format = resolveSessionFormat(tx?.attributes?.protectedData || {});
    rows.push({
      id,
      when: paymentWhen(tx),
      name: talkSessionOtherPartyDisplayName({
        isCustomer: true,
        listing: tx?.listing,
        provider: tx?.provider,
      }),
      format,
      amount: payinMoney(tx),
      status,
    });
  });
  return rows.sort((a, b) => {
    const aMs = a.when ? a.when.getTime() : 0;
    const bMs = b.when ? b.when.getTime() : 0;
    return bMs - aMs;
  });
};
